import React, { FC, createContext, useContext, useEffect, useMemo, useState } from 'react';
import { QueryClient, QueryClientProvider } from 'react-query';
import { useIsClient } from '@fiverr-private/hooks';
import { RecommendedSeller } from '../../../types/seller';
import { InitialProps } from '../../../types/initialProps';
import { useSessionStorage, SessionDataKeys } from '../../hooks/useSessionStorage';

interface LandingPageContextValue extends InitialProps {
    briefBoxInputValue: string | null;
    setBriefBoxInputValue: (value: string | null) => void;
    isBottomDrawerOpen: boolean;
    setIsBottomDrawerOpen: (isOpen: boolean) => void;
    generatedBrief: string | null;
    setGeneratedBrief: (value: string | null) => void;
    sellers: RecommendedSeller[];
    setSellers: (sellers: RecommendedSeller[]) => void;
    isMatchSellersLoading: boolean;
    setIsMatchSellersLoading: (isLoading: boolean) => void;
    sellerIdsToExclude: number[];
    setSellerIdsToExclude: (sellerIdsToExclude: number[]) => void;
    error: string | null;
    setError: (error: string | null) => void;
}

export const LandingPageContext = createContext<LandingPageContextValue | null>(null);

const queryClient = new QueryClient();

export const LandingPageProvider: FC<InitialProps> = ({ children, ...initialProps }) => {
    const [briefBoxInputValue, setBriefBoxInputValue] = useState<string | null>(null);
    const [isBottomDrawerOpen, setIsBottomDrawerOpen] = useState<boolean>(false);
    const [generatedBrief, setGeneratedBrief] = useState<string | null>(null);
    const [isMatchSellersLoading, setIsMatchSellersLoading] = useState<boolean>(false);
    const [sellers, setSellers] = useState<RecommendedSeller[]>([]);
    const [sellerIdsToExclude, setSellerIdsToExclude] = useState<number[]>([]);
    const [error, setError] = useState<string | null>(null);
    const isClient = useIsClient();
    const { popStorageData } = useSessionStorage();

    const contextValue = useMemo(
        () => ({
            ...initialProps,
            briefBoxInputValue,
            setBriefBoxInputValue,
            isBottomDrawerOpen,
            setIsBottomDrawerOpen,
            generatedBrief,
            setGeneratedBrief,
            sellers,
            setSellers,
            sellerIdsToExclude,
            setSellerIdsToExclude,
            isMatchSellersLoading,
            setIsMatchSellersLoading,
            error,
            setError,
        }),
        [
            initialProps,
            briefBoxInputValue,
            generatedBrief,
            sellers,
            sellerIdsToExclude,
            isBottomDrawerOpen,
            isMatchSellersLoading,
            error,
        ]
    );

    useEffect(() => {
        if (isClient) {
            const pageData = popStorageData(SessionDataKeys.PAGE_DATA);
            if (pageData) {
                setBriefBoxInputValue(pageData.briefBoxInputValue || null);
                setGeneratedBrief(pageData.generatedBrief || null);
                setSellers(pageData.sellers || []);
                setSellerIdsToExclude(pageData.sellerIdsToExclude || []);
                setIsBottomDrawerOpen(true);
            }
        }
    }, [isClient, popStorageData]);

    return (
        <LandingPageContext.Provider value={{ ...contextValue }}>
            <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
        </LandingPageContext.Provider>
    );
};

export const useLandingPage = () => {
    const context = useContext(LandingPageContext);
    if (!context) {
        throw new Error('useLandingPage must be used within a LandingPageProvider');
    }
    return context;
};
